import {
  AddCorrelationsModal,
  CorrelationsDetails,
  CorrelationsHeader
} from '@bo-components/correlations';
import { useEffect, useState } from 'react';
import type { ComboboxOption } from '@library/components/selects';
import type { CorrelationsRequest } from '@library/types/correlations';
import InfiniteScroll from 'react-infinite-scroll-component';
import { NoResults } from '@library/components/NoResults';
import { PrimaryButton } from '@library/components/buttons/PrimaryButton';
import { SubmitResetBar } from '@library/components/SubmitResetBar';
import { Tooltip } from '@library/components/tooltip/Tooltip';
import { getDisableSave } from '@bo-utils/correlations';
import { selectHasEditCorrelations } from '@bo-selectors/correlations';
import { useCorrelations } from '@bo-hooks/correlations/useCorrelations';
import { useSelector } from 'react-redux';
import { useUpdateCorrelations } from '@bo-hooks/correlations/useUpdateCorrelations';

export interface CorrelationsProps {
  allStatTypes: Array<ComboboxOption>;
  queryParams: CorrelationsRequest;
}

export const Correlations = ({
  allStatTypes,
  queryParams
}: CorrelationsProps) => {
  const {
    correlations,
    currentPage,
    handleCorrelationsChange,
    handleCorrelationsRefetch,
    hasModifiedCorrelations,
    handlePageChange,
    handleResetCorrelations,
    isRunning,
    totalChanges,
    totalPages
  } = useCorrelations({ queryParams });

  const [showAddCorrelationsModal, setShowAddCorrelationsModal] =
    useState(false);

  const {
    handleResetUpdateCorrelationsStatus,
    handleUpdateCorrelations,
    updateCorrelationsStatus: { errors, isError, isLoading, isSuccess, message }
  } = useUpdateCorrelations({
    handleCorrelationsRefetch,
    handleResetCorrelations
  });

  const handleAddCorrelationsClick = () => {
    if (!isLoading && (isError || isSuccess)) {
      handleResetUpdateCorrelationsStatus();
    }

    setShowAddCorrelationsModal(true);
  };

  const hasEditCorrelations = useSelector(selectHasEditCorrelations);

  useEffect(() => {
    if (hasModifiedCorrelations) {
      handleResetUpdateCorrelationsStatus();
    }
  }, [handleResetUpdateCorrelationsStatus, hasModifiedCorrelations]);

  const disableSave = getDisableSave({ correlations });
  const saveErrors = errors.length
    ? errors.map((error) => JSON.stringify(error))
    : '';

  return (
    <div className="size-full pl-4 pt-4">
      {hasEditCorrelations && (
        <>
          <div className="flex w-full items-center justify-between p-2">
            {isError && (
              <Tooltip position="below" text={saveErrors} title="Errors">
                <p className="text-roses p-6 underline">{`Error: ${message ?? ''}`}</p>
              </Tooltip>
            )}
            {isSuccess && (
              <p className="text-atlien-200 p-6">
                Correlations Published Successfully
              </p>
            )}
            <PrimaryButton
              className="mb-2 px-3 py-2 text-base font-medium"
              disabled={hasModifiedCorrelations}
              handleOnClick={handleAddCorrelationsClick}
              title="Add Correlations"
            >
              Add Correlations
            </PrimaryButton>
          </div>
          {totalChanges > 0 && (
            <SubmitResetBar
              className="mb-4"
              cancelText="Reset Changes"
              handlePublish={() =>
                handleUpdateCorrelations({
                  correlations: correlations.filter(
                    (correlation) => !!correlation.rowStatus
                  )
                })
              }
              handleReset={handleResetCorrelations}
              prefix={
                <span className="text-soClean-100 pl-6 text-sm">{`${
                  totalChanges
                } Modified Correlation${totalChanges > 1 ? 's' : ''}`}</span>
              }
              saveDisabled={isLoading || disableSave}
            />
          )}
        </>
      )}
      <div
        className="h-[calc(100%-64px)] w-full overflow-scroll"
        id="scrollableDiv"
      >
        {!isRunning && !correlations.length ? (
          <div>
            <NoResults />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={correlations.length}
            next={handlePageChange}
            hasMore={(totalPages ?? 0) > currentPage}
            loader={<p>Loading...</p>}
            endMessage={<span />}
            scrollableTarget="scrollableDiv"
          >
            <table>
              <CorrelationsHeader />
              <CorrelationsDetails
                allStatTypes={allStatTypes}
                errors={errors}
                hasEditCorrelations={hasEditCorrelations}
                updatedCorrelations={correlations}
                onUpdateCorrelationsChange={handleCorrelationsChange}
              />
            </table>
          </InfiniteScroll>
        )}
        {showAddCorrelationsModal && (
          <AddCorrelationsModal
            allStatTypes={allStatTypes}
            handleCorrelationsRefetch={handleCorrelationsRefetch}
            handleResetCorrelations={handleResetCorrelations}
            isOpen={showAddCorrelationsModal}
            onCancel={() => setShowAddCorrelationsModal(false)}
          />
        )}
      </div>
    </div>
  );
};
